import * as React from "react";
import { cn } from "../lib/utils";

/**
 * Color definitions for categorical badges. Each entry maps a color name
 * to its CSS custom property (from ui-tokens) and background opacity.
 */
const CATEGORY_COLOR_DEFS = {
  blue: { token: "--cat-blue", bgOpacity: 0.18 },
  "blue-light": { token: "--cat-blue-light", bgOpacity: 0.18 },
  purple: { token: "--cat-purple", bgOpacity: 0.18 },
  green: { token: "--cat-green", bgOpacity: 0.18 },
  amber: { token: "--cat-amber", bgOpacity: 0.18 },
  teal: { token: "--cat-teal", bgOpacity: 0.18 },
  orange: { token: "--cat-orange", bgOpacity: 0.18 },
  slate: { token: "--cat-slate", bgOpacity: 0.18 },
  emerald: { token: "--cat-emerald", bgOpacity: 0.15 },
  violet: { token: "--cat-violet", bgOpacity: 0.15 },
  diamond: { token: "--cat-diamond", bgOpacity: 0.18 },
} as const;

type CategoryColor = keyof typeof CATEGORY_COLOR_DEFS;

const CATEGORY_COLORS = Object.keys(CATEGORY_COLOR_DEFS) as CategoryColor[];

interface CategoryBadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  color: CategoryColor;
  children: React.ReactNode;
}

function CategoryBadge({ color, className, style, children, ...props }: CategoryBadgeProps) {
  const def = CATEGORY_COLOR_DEFS[color];
  const varRef = `var(${def.token})`;

  // WCAG 2.2 AA SC 1.4.3 (≥ 4.5:1): the background is a low-opacity tint of the category hue, so
  // same-hue text never clears 4.5:1. Mixing 40% with --color-foreground — near-black in light
  // mode, near-white in dark — darkens or lightens to suit, clearing 4.5:1 for every color in
  // CATEGORY_COLOR_DEFS in both themes (verified via axe-core / scripts/a11y-audit.mjs).
  const textColor = `color-mix(in srgb, ${varRef} 40%, var(--color-foreground) 60%)`;
  const bgColor = `color-mix(in srgb, ${varRef} ${Math.round(def.bgOpacity * 100)}%, transparent)`;

  return (
    <span
      className={cn(
        "inline-block rounded-[3px] px-1.5 py-0.5 text-[0.75rem] font-medium tracking-wider",
        className,
      )}
      style={{
        color: textColor,
        backgroundColor: bgColor,
        ...style,
      }}
      {...props}
    >
      {children}
    </span>
  );
}

export { CATEGORY_COLORS, CategoryBadge };
export type { CategoryBadgeProps, CategoryColor };
